import { default as React, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  selectFilteredVirtualCards,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/VirtualCardsNeedsApproval/redux/virtualCardsNeedsApproval.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import PendingRequestsBox from 'src/components/PendingRequestsBox/PendingRequestsBox'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { VirtualCardTypes } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { useQueryOrgApprovalRules } from 'src/queries/organizations/cardApproverRules.queries'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectBillPayCardsPendingRequestsCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const BillPayNeedsApproval: React.FC = () => {
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const appName = useSelector(selectAppName)
  const user = useCurrentUser()
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const billPayCardsPendingRequestCount = useSelector(
    selectBillPayCardsPendingRequestsCountStatisticsFromState,
  )
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.BillPayCardsPendingRequestsCountStatistics,
    ])
  }, [dispatchStatistics])

  const dispatch = useBackgroundDispatch()

  const { data, isSuccess } = useQueryOrgApprovalRules({
    orgId: user?.organizationId,
  })
  const isUserOrganizationCardApprover = data?.approverId === user?.id

  const fetchVirtualCards = (
    filtersBody: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    if (isSuccess) {
      dispatch(
        getVirtualCards({
          count: 10,
          page: page,
          cardholder: 'me',
          pendingRequest: true,
          ...filtersBody,
          cardTypes: [VirtualCardTypes.billPay],
        }),
      )
    }
  }
  const fetchVirtualCardsForCardApprover = (
    filtersBody: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        count: 10,
        page: page,
        cardholderPendingRequestOrApproverRuleApproval: 'me',
        ...filtersBody,
        cardTypes: [VirtualCardTypes.billPay],
      }),
    )
  }

  const infiniteScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCards,
  }

  const cardApproverScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCardsForCardApprover,
  }

  if (!isUserOrganizationCardApprover) return <NotFoundBackgroundPageContent />

  return (
    <>
      <Helmet>
        <title>{`Bill Pay | ${appName}`}</title>
        <meta name="description" content={`Bill Pay page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent noPadding>
        <PendingRequestsBox
          virtualCards={filteredVirtualCards}
          infiniteScrollProps={infiniteScrollProps}
          isUserCardApprover={isUserOrganizationCardApprover}
          cardApprovalRulesFetched={isSuccess}
          cardApproverScrollProps={cardApproverScrollProps}
          showLoadingPage={
            !statisticsLoaded.billPayCardsPendingRequestsCountStatisticsLoaded
          }
          totalCount={billPayCardsPendingRequestCount}
        />
      </BackgroundPageContent>
    </>
  )
}

export default BillPayNeedsApproval
